import * as React from 'react';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Keyboard,
    type LayoutChangeEvent,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    Platform,
    type ScrollView as RNScrollView,
    useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    type GetProps,
    isWeb,
    ScrollView,
    Sheet as TamaguiSheet,
} from 'tamagui';

import { isNonMobileWeb } from '../../constants';
import { SimpleModal } from '../modals/Modals';
import { KeyboardCompatibleBottomSheet } from './KeyboardCompatibleBottomSheet';
import {
    getMaxSheetHeight,
    getPaddingForScrollGradient,
    SheetContent,
    SheetScrollGradient,
    WEB_SHEET_MIN_HEIGHT,
} from './SheetContent';
import { getSheetHeaderHeight, SheetHeader } from './SheetHeader';

const SheetOverlay = () => {
    return (
        <TamaguiSheet.Overlay
            top={0}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            animation="sheet"
            animateOnly={['opacity']}
            opacity={1}
        />
    );
};

const SheetHandle = () => {
    return (
        <TamaguiSheet.Handle
            top={20}
            backgroundColor="$gray1000"
            opacity={0.5}
            width={30}
            height={5}
            marginHorizontal="auto"
        />
    );
};

interface SheetFrameProps extends GetProps<typeof TamaguiSheet.Frame> {}

const SheetFrame = ({ children, ...props }: SheetFrameProps) => {
    return (
        <TamaguiSheet.Frame
            disableHideBottomOverflow={true}
            borderTopLeftRadius={30}
            borderTopRightRadius={30}
            paddingHorizontal={0}
            paddingTop={0}
            paddingBottom={0}
            {...props}
        >
            {children}
        </TamaguiSheet.Frame>
    );
};

export interface SmoothieSheetProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    disableDrag?: boolean;
    dismissOnOverlayPress?: boolean;
    renderInAppRoot?: boolean;
    isInsideModal?: boolean;
    forceKeyboardCompatibleBottomSheet?: boolean;
    withPadding?: boolean;
    title?: string;
    subtitle?: string;
    showCloseButton: boolean;
    headerLeftAction?: React.ReactNode;
    fixedContentHeight?: number;
    forceMaxContentHeight?: boolean;
    // See KeyboardCompatibleBottomSheet's own doc comment on this prop — only
    // has an effect there (the TamaguiSheet/SimpleModal paths ignore it).
    keyboardScrollFallback?: boolean;
    contentScrollRef?: React.RefObject<RNScrollView | null>;
    onContentScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
    scrollContainerOverride?: (children: ReactNode) => ReactNode;
    titleLineHeight?: number;
}

export const Sheet = ({
    isOpen,
    onClose,
    children,
    disableDrag = false,
    dismissOnOverlayPress = true,
    renderInAppRoot = true,
    // NOTE: this default has flipped from false to true so we can assess whether we need the tamagui sheet at all
    // Our belief is that the keyboardCompatibleBottomSheet is just more reliable and problem-free for us to use everywhere
    forceKeyboardCompatibleBottomSheet = true,
    isInsideModal,
    withPadding = true,
    title,
    subtitle,
    showCloseButton,
    headerLeftAction,
    fixedContentHeight,
    forceMaxContentHeight,
    keyboardScrollFallback,
    contentScrollRef,
    onContentScroll,
    scrollContainerOverride,
    titleLineHeight,
}: SmoothieSheetProps) => {
    const { top, bottom } = useSafeAreaInsets();
    const { height: screenHeight } = useWindowDimensions();
    const [shouldShowScrollGradient, setShouldShowScrollGradient] =
        useState(false);

    const paddingForScrollGradient = useMemo(() => {
        return getPaddingForScrollGradient(shouldShowScrollGradient, bottom);
    }, [shouldShowScrollGradient, bottom]);
    const maxSheetHeight = useMemo(() => {
        return getMaxSheetHeight(screenHeight, top);
    }, [screenHeight, top]);
    const sheetContentMaxHeight = useMemo(() => {
        return (
            maxSheetHeight -
            getSheetHeaderHeight({
                title,
                showCloseButton,
                leftAction: headerLeftAction,
                withPadding,
            })
        );
    }, [title, showCloseButton, headerLeftAction, withPadding, maxSheetHeight]);
    const contentHeight = useMemo(() => {
        return forceMaxContentHeight
            ? sheetContentMaxHeight
            : fixedContentHeight;
    }, [forceMaxContentHeight, fixedContentHeight, sheetContentMaxHeight]);

    const useKeyboardCompatibleBottomSheet =
        forceKeyboardCompatibleBottomSheet ||
        // If we're on iOS and the Sheet is inside a modal, we use a KeyboardCompatibleBottomSheet instead
        // because this underlying component has special support for this layering scenario
        (Platform.OS === 'ios' && isInsideModal);

    useEffect(() => {
        // Dismiss keyboard in case it's open as sheet is opening.
        // This only works for KeyboardCompatibleBottomSheet, not TamaguiSheet (it ends up dismissing the TamaguiSheet incidentally too)
        if (useKeyboardCompatibleBottomSheet) {
            Keyboard.dismiss();
        }
    }, [isOpen]);

    const onContentLayout = useCallback(
        (event: LayoutChangeEvent) => {
            const { height } = event.nativeEvent.layout;
            if (height != null && height > sheetContentMaxHeight) {
                setShouldShowScrollGradient(true);
            }
        },
        [sheetContentMaxHeight],
    );

    const buildSheetContent = (
        ScrollComponent: typeof ScrollView | typeof TamaguiSheet.ScrollView,
        height?: number,
    ) => {
        return (
            <>
                <SheetHeader
                    withPadding={withPadding}
                    title={title}
                    subtitle={subtitle}
                    showCloseButton={showCloseButton}
                    onClose={onClose}
                    leftAction={headerLeftAction}
                    titleLineHeight={titleLineHeight}
                />
                {scrollContainerOverride != null ? (
                    scrollContainerOverride(
                        <SheetContent
                            withPadding={withPadding}
                            height={height ?? '100%'}
                            maxHeight={sheetContentMaxHeight}
                            shouldShowScrollGradient={false}
                        >
                            {children}
                        </SheetContent>,
                    )
                ) : (
                    <ScrollComponent
                        ref={
                            contentScrollRef as React.Ref<
                                React.ComponentRef<typeof ScrollComponent>
                            >
                        }
                        maxHeight={sheetContentMaxHeight}
                        onScroll={onContentScroll}
                        scrollEventThrottle={
                            typeof onContentScroll === 'function'
                                ? 16
                                : undefined
                        }
                        showsVerticalScrollIndicator={isWeb}
                        keyboardShouldPersistTaps="handled"
                        contentContainerStyle={{
                            paddingBottom: paddingForScrollGradient,
                        }}
                    >
                        <SheetContent
                            withPadding={withPadding}
                            // minHeight (not height) so short content still fills
                            // the sheet, but taller content — or the same content
                            // once the keyboard shrinks the visible area — can grow
                            // past that and actually become scrollable. A fixed
                            // height here clamps content to the viewport, leaving
                            // nothing for the ScrollView to scroll to and cutting
                            // off anything (e.g. Save/Cancel) below the fold.
                            minHeight={height ?? '100%'}
                            shouldShowScrollGradient={shouldShowScrollGradient}
                            onLayout={onContentLayout}
                        >
                            {children}
                        </SheetContent>
                    </ScrollComponent>
                )}
                {shouldShowScrollGradient && <SheetScrollGradient />}
            </>
        );
    };

    // If we're on web with a large screen, we use a SimpleModal instead of Sheet always
    // If we're on mobile web isInsideModal is true, we use a SimpleModal because otherwise the Sheet
    // cannot be layered on top of the modal
    if (isNonMobileWeb || (isWeb && isInsideModal)) {
        return (
            <SimpleModal
                isOpen={isOpen}
                onClose={onClose}
                dismissOnOverlayPress={dismissOnOverlayPress}
                renderInAppRoot={renderInAppRoot}
                withPadding={false}
                dialogProps={{
                    overflow: 'hidden',
                    minHeight: WEB_SHEET_MIN_HEIGHT,
                    maxHeight: maxSheetHeight + 2, // + 2 adjustment prevents unnecessary scrollbars
                    onOpenAutoFocus: (e: Event) => {
                        e.preventDefault();
                    },
                }}
            >
                {buildSheetContent(ScrollView, contentHeight)}
            </SimpleModal>
        );
    }

    if (useKeyboardCompatibleBottomSheet) {
        return isOpen ? (
            <KeyboardCompatibleBottomSheet
                withPadding={withPadding}
                onDismiss={onClose}
                dismissOnBackdropPress={dismissOnOverlayPress}
                isInsideModal={isInsideModal}
                title={title}
                subtitle={subtitle}
                titleLineHeight={titleLineHeight}
                showCloseButton={showCloseButton}
                headerLeftAction={headerLeftAction}
                fixedContentHeight={fixedContentHeight}
                forceMaxContentHeight={forceMaxContentHeight}
                keyboardScrollFallback={keyboardScrollFallback}
                disableDrag={disableDrag}
                contentScrollRef={contentScrollRef}
                onContentScroll={onContentScroll}
                scrollContainerOverride={scrollContainerOverride}
            >
                {children}
            </KeyboardCompatibleBottomSheet>
        ) : null;
    }

    return (
        <TamaguiSheet
            open={isOpen}
            snapPointsMode="fit"
            onOpenChange={onClose}
            animation="sheet"
            dismissOnSnapToBottom
            modal
            disableDrag={disableDrag}
            dismissOnOverlayPress={dismissOnOverlayPress}
        >
            <SheetOverlay />
            {!disableDrag && <SheetHandle />}
            <SheetFrame>
                {buildSheetContent(TamaguiSheet.ScrollView, contentHeight)}
            </SheetFrame>
        </TamaguiSheet>
    );
};
